import client from 'enrollment-platform/api/eps.client'
import { useMutation, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useSelector } from 'react-redux'
import { selectors as companySelectors } from 'store/modules/companies'
import { notification } from '@surecompanies/core_components'
import { queryClient } from 'containers/AppContainer'
import { useState } from 'react'

export type AssistedPayrollSyncResult = {
  id: number
  status: string
  startedAt: string
  completedAt: string
  fileContent: any
}

export type AssistedPayrollSyncResultListItem = {
  id: number
  clientPublicKey: string
  benefitEffectiveReport: string
  dateRangeStart: string
  dateRangeEnd: string
  status: string
  startedByName: string
  assistedPayrollSyncResult: AssistedPayrollSyncResult
}

type Paginated<T> = {
  data: T[]
  page: number
  limit: number
  total: number
}

type UseAssistedOpts = {
  page?: number
  limit?: number
  clientPublicKey?: string
}

export default function useAssistedPayroll(opts: UseAssistedOpts = {}) {
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [isUploadResultModalOpen, setIsUploadResultModalOpen] = useState(false)
  const [selectedPayrollId, setSelectedPayrollId] = useState<number | null>(
    null
  )
  const companyList = useSelector(
    companySelectors.companyCollectionSelector
  )?.map((company) => ({
    label: company.companyName,
    value: company.id,
  }))

  const companyMap = useSelector(companySelectors.companyMapSelector)

  const page = opts.page ?? 1
  const limit = opts.limit ?? 10

  const keyClient = opts.clientPublicKey ?? ''

  async function getAssistedPayrollSyncs(): Promise<
    Paginated<AssistedPayrollSyncResultListItem>
  > {
    try {
      const response = await client.get('/payroll/manual', {
        params: {
          page,
          limit,
          clientPublicKey: keyClient || undefined,
        },
      })
      const body = response.data ?? {}
      const meta = body.meta ?? {}

      return {
        data: Array.isArray(body.data) ? body.data : [],
        page: Number(meta.page ?? page) || 1,
        limit: Number(meta.limit ?? limit) || 10,
        total: Number(meta.total ?? 0) || 0,
      }
    } catch (err) {
      const message = 'Failed to load payroll entries'
      notification.error({ message })
      return { data: [], page, limit, total: 0 }
    }
  }

  const { isPending, error, data } = useQuery({
    queryKey: ['getAssistedPayroll', page, limit, keyClient],
    queryFn: getAssistedPayrollSyncs,
    staleTime: 0,
  })

  async function initiatePayrollSync(params: {
    payrollDates: {
      startDate: string
      endDate: string
    }[]
    clientPublicKey: string
  }) {
    try {
      const [startDate, endDate] = params.payrollDates
      const dateRangeStart = dayjs(startDate as unknown as dayjs.Dayjs).format(
        'YYYY-MM-DD'
      )
      const dateRangeEnd = dayjs(endDate as unknown as dayjs.Dayjs).format(
        'YYYY-MM-DD'
      )
      const { data } = await client.post('/payroll/manual', {
        dateRangeStart,
        dateRangeEnd,
        clientPublicKey: params.clientPublicKey,
        companyName: companyMap[params.clientPublicKey]?.companyName,
      })
      notification.success({
        message: 'Payroll sync successfully created',
      })
      queryClient.invalidateQueries({ queryKey: ['getAssistedPayroll'] })
      return data
    } catch (err) {
      notification.error({
        message: 'Failed to create payroll sync',
      })
    }
  }

  const { mutate: triggerPayrollSync, isPending: isTriggerPayrollSyncPending } =
    useMutation({
      mutationFn: (params: {
        payrollDates: {
          startDate: string
          endDate: string
        }[]
        clientPublicKey: string
      }) => initiatePayrollSync(params),
    })

  const getPayrollDownload = async (
    id: number,
    companyName: string,
    date: string
  ): Promise<{ blob: Blob; filename: string }> => {
    const { data } = await client.get(`/payroll/manual/${id}/url`)
    const fileResp = await fetch(data.url)
    if (!fileResp.ok) {
      throw new Error(`Failed to fetch payroll file: ${fileResp.status}`)
    }
    const blob = await fileResp.blob()
    const safeCompanyName = companyName
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^A-Za-z0-9._-]/g, '')
    const filename = `${date}_${safeCompanyName}-contributions.csv`
    return { blob, filename }
  }

  const openUploadResultModal = (id: number) => {
    setSelectedPayrollId(id)
    setIsUploadResultModalOpen(true)
  }

  const onViewDetails = (id: number) => {
    setSelectedPayrollId(id)
    setIsDetailsModalOpen(true)
  }

  const processPayroll = async (id: number) => {
    try {
      await client.post(`/payroll/manual/${id}/process`)
      notification.success({
        message: 'Payroll processed successfully',
      })
      queryClient.invalidateQueries({ queryKey: ['getAssistedPayroll'] })
    } catch (err) {
      notification.error({
        message: 'Failed to process payroll',
      })
    }
  }

  const { mutate: process, isPending: isProcessing } = useMutation({
    mutationFn: (id: number) => processPayroll(id),
  })

  return {
    isPending,
    error,
    data: data?.data ?? [],
    total: Number(data?.total ?? 0),
    page: Number(data?.page ?? page),
    limit: Number(data?.limit ?? limit),
    triggerPayrollSync,
    companyList,
    companyMap,
    isCreatingPayrollSync: isTriggerPayrollSyncPending,
    onViewDetails,
    setSelectedPayrollId,
    isDetailsModalOpen,
    closeDetailsModal: () => {
      setIsDetailsModalOpen(false)
      setSelectedPayrollId(null)
    },
    getPayrollDownload,
    isUploadResultModalOpen,
    openUploadResultModal,
    selectedPayrollId,
    closeUploadResultModal: () => {
      setIsUploadResultModalOpen(false)
      setSelectedPayrollId(null)
    },
    processPayroll: process,
    isProcessing,
  }
}
